import { useEffect, useRef, useState } from "react";
import { getSettings, saveSettings, uploadLogo, removeLogo, logoUrl, apiError } from "@/lib/api";
import { PageHeader, Card, Loading } from "@/components/Shared";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Building2, Landmark, Percent, FileText, Save, Image, Bell, Upload, Trash2 } from "lucide-react";

function Section({ icon: Icon, title, children }) {
  return (
    <Card className="p-5">
      <div className="flex items-center gap-2.5 mb-4">
        <span className="w-9 h-9 rounded-lg bg-[#F1F5F9] text-[#0B132B] flex items-center justify-center"><Icon className="w-4 h-4" /></span>
        <h3 className="font-display font-semibold text-[#0B132B]">{title}</h3>
      </div>
      <div className="space-y-4">{children}</div>
    </Card>
  );
}

export default function Settings() {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [logoBust, setLogoBust] = useState(Date.now());
  const fileRef = useRef(null);
  useEffect(() => { getSettings().then(setS).catch((e) => toast.error(apiError(e))); }, []);
  const set = (k, num) => (e) => setS({ ...s, [k]: num ? Number(e.target.value) : e.target.value });

  const save = async () => {
    setSaving(true);
    try { const upd = await saveSettings(s); setS(upd); toast.success("Settings saved"); }
    catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };

  const onLogoFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!["image/png", "image/jpeg", "image/jpg", "image/webp"].includes(file.type)) return toast.error("Use PNG, JPG, JPEG or WEBP.");
    if (file.size > 5 * 1024 * 1024) return toast.error("Max size is 5MB.");
    setUploading(true);
    try { await uploadLogo(file); const upd = await getSettings(); setS(upd); setLogoBust(Date.now()); toast.success("Logo uploaded"); }
    catch (err) { toast.error(apiError(err)); } finally { setUploading(false); if (fileRef.current) fileRef.current.value = ""; }
  };
  const onRemoveLogo = async () => {
    try { await removeLogo(); const upd = await getSettings(); setS(upd); setLogoBust(Date.now()); toast.success("Logo removed"); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (!s) return <Loading />;
  const hasLogo = !!s.logo_path;

  return (
    <div>
      <PageHeader title="Business Settings" subtitle="Configure your company profile, banking and document defaults.">
        <Button onClick={save} disabled={saving} className="bg-[#0B132B] hover:bg-[#1C2541]" data-testid="save-settings-button"><Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving…" : "Save Changes"}</Button>
      </PageHeader>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Section icon={Building2} title="Company Details">
          <div><Label>Company Name</Label><Input value={s.company_name || ""} onChange={set("company_name")} className="mt-1.5" data-testid="settings-company-name" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Email</Label><Input value={s.email || ""} onChange={set("email")} className="mt-1.5" data-testid="settings-email" /></div>
            <div><Label>Cell</Label><Input value={s.cell || ""} onChange={set("cell")} className="mt-1.5" data-testid="settings-cell" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>WhatsApp</Label><Input value={s.whatsapp || ""} onChange={set("whatsapp")} className="mt-1.5" data-testid="settings-whatsapp" /></div>
            <div><Label>Logo URL</Label><Input value={s.logo_url || ""} onChange={set("logo_url")} placeholder="https://…" className="mt-1.5" data-testid="settings-logo" /></div>
          </div>
          <div><Label>Address</Label><Input value={s.address || ""} onChange={set("address")} className="mt-1.5" data-testid="settings-address" /></div>
        </Section>

        <Section icon={Image} title="Business Logo">
          <div className="flex items-center gap-4">
            <div className="w-28 h-20 rounded-lg border border-slate-200 bg-[#F1F5F9] flex items-center justify-center overflow-hidden shrink-0" data-testid="logo-preview">
              {hasLogo ? <img src={`${logoUrl()}&b=${logoBust}`} alt="Logo preview" className="max-h-full max-w-full object-contain" /> : s.logo_url ? <img src={s.logo_url} alt="Logo preview" className="max-h-full max-w-full object-contain" /> : <span className="text-xs text-slate-400">No logo</span>}
            </div>
            <div className="space-y-2">
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={onLogoFile} className="hidden" data-testid="logo-file-input" />
              <Button type="button" variant="outline" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="logo-upload-button">
                <Upload className="w-4 h-4 mr-1.5" /> {uploading ? "Uploading…" : hasLogo ? "Replace Logo" : "Upload Logo"}
              </Button>
              {hasLogo && <Button type="button" variant="ghost" className="text-rose-500 ml-2" onClick={onRemoveLogo} data-testid="logo-remove-button"><Trash2 className="w-4 h-4 mr-1.5" /> Remove</Button>}
              <p className="text-xs text-slate-400">PNG, JPG or WEBP · max 5MB · used on documents & PDFs.</p>
            </div>
          </div>
        </Section>

        <Section icon={Landmark} title="Banking Details">
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Bank Name</Label><Input value={s.bank_name || ""} onChange={set("bank_name")} className="mt-1.5" data-testid="settings-bank" /></div>
            <div><Label>Account Name</Label><Input value={s.account_name || ""} onChange={set("account_name")} className="mt-1.5" data-testid="settings-account-name" /></div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Account Number</Label><Input value={s.account_number || ""} onChange={set("account_number")} className="mt-1.5" data-testid="settings-account-number" /></div>
            <div><Label>Branch Code</Label><Input value={s.branch_code || ""} onChange={set("branch_code")} className="mt-1.5" data-testid="settings-branch" /></div>
          </div>
        </Section>

        <Section icon={Percent} title="VAT & Currency">
          <div className="flex items-center justify-between bg-[#F1F5F9] rounded-lg px-4 py-3">
            <div><div className="text-sm font-medium text-[#0B132B]">VAT Enabled</div><div className="text-xs text-slate-500">Apply VAT to new documents by default</div></div>
            <Switch checked={!!s.default_vat_enabled} onCheckedChange={(v) => setS({ ...s, default_vat_enabled: v })} data-testid="settings-vat-toggle" />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>VAT Rate (%)</Label><Input type="number" step="0.01" value={s.vat_rate || 0} onChange={set("vat_rate", true)} className="mt-1.5" data-testid="settings-vat-rate" /></div>
            <div><Label>VAT Number</Label><Input value={s.vat_number || ""} onChange={set("vat_number")} className="mt-1.5" data-testid="settings-vat-number" /></div>
          </div>
          <div><Label>Currency Symbol</Label><Input value={s.currency || "R"} onChange={set("currency")} className="mt-1.5 w-28" data-testid="settings-currency" /></div>
        </Section>

        <Section icon={FileText} title="Document Settings">
          <div className="grid grid-cols-3 gap-3">
            <div><Label>Invoice Prefix</Label><Input value={s.invoice_prefix || ""} onChange={set("invoice_prefix")} className="mt-1.5" data-testid="settings-invoice-prefix" /></div>
            <div><Label>Quote Prefix</Label><Input value={s.quote_prefix || ""} onChange={set("quote_prefix")} className="mt-1.5" data-testid="settings-quote-prefix" /></div>
            <div><Label>Payslip Prefix</Label><Input value={s.payslip_prefix || ""} onChange={set("payslip_prefix")} className="mt-1.5" data-testid="settings-payslip-prefix" /></div>
          </div>
          <div><Label>Default Terms / Notes</Label><Textarea value={s.terms || ""} onChange={set("terms")} rows={3} className="mt-1.5" data-testid="settings-terms" /></div>
        </Section>

        <Section icon={Bell} title="Overdue Reminders">
          <p className="text-xs text-slate-500 -mt-1">Automatically email customers when an invoice becomes overdue. Reminders never send for paid or cancelled invoices, and each reminder sends only once.</p>
          {[["reminder_1_enabled", "First reminder", "1 day after due date"], ["reminder_2_enabled", "Second reminder", "7 days after due date"], ["reminder_3_enabled", "Third reminder", "14 days after due date"]].map(([key, label, sub]) => (
            <div key={key} className="flex items-center justify-between bg-[#F1F5F9] rounded-lg px-4 py-3">
              <div><div className="text-sm font-medium text-[#0B132B]">{label}</div><div className="text-xs text-slate-500">{sub}</div></div>
              <Switch checked={!!s[key]} onCheckedChange={(v) => setS({ ...s, [key]: v })} data-testid={`toggle-${key}`} />
            </div>
          ))}
        </Section>
      </div>
    </div>
  );
}
